import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import cm from 'classnames';

import styles from './_hover-button.module.scss';
import { useAppSelector } from 'store/store-hooks';

interface Props {
  button: ReactNode;
  className?: string;
  isEnabled?: boolean;
  children: React.ReactNode;
  positionRight?: boolean;
  dots?: boolean;
}

export const HoverBtn = ({ className, button, isEnabled = true, children, dots = true, positionRight }: Props) => {
  const heldKey = useAppSelector((state) => state.app.heldKey);
  const [isHover, setIsHover] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const active = isActive && isEnabled;

  useEffect(() => {
    setIsActive(isHover && heldKey === 'Shift');
  }, [isHover, heldKey]);

  const len = useMemo(() => {
    return React.Children.count(children);
  }, []);

  const selected = useMemo(() => {
    if (!active)
      return React.Children.toArray(children).findIndex((el) => {
        if (React.isValidElement(el)) return el.props.selected;
      });
  }, [active]);

  return (
    <div
      className={cm(styles.HoverBtn, className)}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
    >
      <div className={styles.HoverBtn__header}>
        {button}
        {dots && (
          <ul className={styles.HoverBtn__dots}>
            {Array(len)
              .fill(0)
              .map((_, i) => (
                <li key={i} className={cm({ [styles.selected]: i === selected })} />
              ))}
          </ul>
        )}
      </div>
      <div
        className={cm(styles.HoverBtn__body, {
          [styles['HoverBtn__body--active']]: isActive && isEnabled,
          [styles['HoverBtn__body--position-right']]: positionRight,
        })}
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.stopPropagation();
          setIsActive(false);
        }}
      >
        {children}
      </div>
    </div>
  );
};
